import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Avatar,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  LinearProgress,
  AppBar,
  Toolbar,
  CssBaseline,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Collapse,
  Badge,
  CircularProgress,
  Stack,
  Container,
  AlertTitle
} from '@mui/material';
import { 
  Dashboard as DashboardIcon, 
  Menu as MenuIcon,
  QrCode as QrCodeIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as AccessTimeIcon,
  EventAvailable as EventAvailableIcon,
  CalendarToday as CalendarTodayIcon,
  History as HistoryIcon,
  Today as TodayIcon,
  Update as UpdateIcon,
  VpnKey as VpnKeyIcon,
  Receipt as ReceiptIcon,
  FileCopy as FileCopyIcon,
  Storefront as StorefrontIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  FiberManualRecord as FiberManualRecordIcon,
  FolderOpen as FolderOpenIcon,
  Loop as LoopIcon,
  MonetizationOn as MonetizationOnIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Storage as StorageIcon,
  EventNote as EventNoteIcon,
  HelpOutline as HelpOutlineIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../services/authService';
import { collection, query, where, orderBy, limit, onSnapshot, addDoc, serverTimestamp, doc, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import QridManagement from './QridManagement';
import { styled } from '@mui/material/styles';
import { QridRecord, addStoreAction } from '../services/qridService';
import { getInvoiceRecordsByQrid, getInvoicesByQridAndType, InvoiceRecord, RefundRecord, getRefundRecordsByQrid } from '../services/invoiceService';
import { getEmhubAccessPassesByQrid, EmhubAccessPass } from '../services/emhubAccessService';
import StoreActions from './StoreActions';
import EmhubAccessPasses from './EmhubAccessPass';
import ModernBookingDashboard from './ModernBookingDashboard';
import BookingFormSettings from './BookingFormSettings';
import TodoManagement from './TodoManagement';
import SeoDashboard from './SeoDashboard';
import ErrorBoundary from './ErrorBoundary';
import FigmaSidebar from './FigmaSidebar';
import { alpha } from '@mui/material/styles';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import { formatDate, getStatusChipColor, getStatusStyle, categorizeDateByTime, shouldExcludeOldCompletedRecord } from '../utils/dashboardUtils';
import { useThemeContext } from '../contexts/ThemeContext';

// Dashboard container styles
const DashboardContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  minHeight: '100vh',
  background: '#f8f9fa',
});

// Main content area
const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
}));

// Drawer width for sidebar
const drawerWidth = 280;

// Styled sidebar drawer
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    background: '#ffffff',
    borderRight: '1px solid #f0f0f0',
  },
}));

// App bar styling
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: '#ffffff',
  color: '#333333',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  zIndex: theme.zIndex.drawer + 1,
}));

// Stats card styling
const StatsCard = styled(Paper)({
  padding: 16,
  borderRadius: 12,
  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

// Compact stat box for mobile
const StatBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  borderRadius: 8,
  backgroundColor: theme.palette.background.paper,
  border: '1px solid #f0f0f0',
}));

// Icon styles
const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgcolor'
})<{ bgcolor?: string }>(({ theme, bgcolor }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bgcolor || theme.palette.primary.main,
  color: '#ffffff',
}));

// Record container styling
const RecordItem = styled(Box)({
  padding: '12px 16px',
  borderBottom: '1px solid #f0f0f0',
  '&:last-child': {
    borderBottom: 'none',
  },
});

// Add these new interfaces
interface StoreAction {
  actionType: 'Pickup' | 'Return' | 'Assist';
  name: string;
  status: string;
  Qrid: string;
  who_collect?: string;
  timestamp: any;
  actionstatus?: 'Pending' | 'In Progress' | 'Completed';
}

interface SearchQRDialogProps {
  open: boolean;
  onClose: () => void;
  onSearch: (searchTerm: string) => void;
  setSnackbar: (snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }) => void;
}

// Function to safely extract the invoice number
function extractInvoiceNumber(doc: any): string {
  if (!doc) return 'Unknown';
  
  // Directly log the document to inspect available fields
  console.log('EXTRACT INVOICE NUMBER FROM:', doc);
  
  // Access the exact field using string index notation
  const invoiceNo = doc['Invoice No'];
  console.log('EXTRACTED INVOICE NO:', invoiceNo);
  
  return invoiceNo || 'Unknown';
}

// Update the SearchQRDialog component to remove scanning functionality
const SearchQRDialog: React.FC<SearchQRDialogProps> = ({ open, onClose, onSearch, setSnackbar }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Function to handle live search and update match count
  useEffect(() => {
    // Don't search with empty terms
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setMatchCount(0);
      return;
    }
    
    setLoading(true);
    
    const term = searchTerm.toLowerCase().trim();
    const recordsRef = collection(db, "QridRecord");
    
    // Create a real-time query for matches
    const unsubscribe = onSnapshot(
      query(recordsRef, orderBy("Timestamp", "desc")),
      (snapshot) => {
        console.log(`Live search - Total documents: ${snapshot.size}`);
        
        // Track all matching records
        const matchingRecords: QridRecord[] = [];
        // Track unique Qrid values to prevent duplicates
        const uniqueQrids = new Set<string>();
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const candidate = { id: doc.id, ...data } as QridRecord;
          if (shouldExcludeOldCompletedRecord(candidate)) {
            return;
          }
          const isMatch = (
            (data.Name?.toLowerCase().includes(term)) ||
            (data.Contact_Number?.includes(term)) ||
            (data["Contact Number"]?.includes(term)) ||
            (data.Qrid?.includes(term))
          );
          
          if (isMatch) {
            matchingRecords.push(data as QridRecord);
            if (data.Qrid) {
              uniqueQrids.add(data.Qrid);
            }
          }
        });
        
        console.log(`Live search - Total matching records: ${matchingRecords.length}`);
        console.log(`Live search - Unique QRids: ${uniqueQrids.size}`);
        
        // Calculate consolidated count: unique QRids + records without Qrid
        const recordsWithoutQrid = matchingRecords.filter(record => !record.Qrid).length;
        const consolidatedCount = uniqueQrids.size + recordsWithoutQrid;
        
        console.log(`Live search - Records without QRid: ${recordsWithoutQrid}`);
        console.log(`Live search - Consolidated count: ${consolidatedCount}`);
        
        setMatchCount(consolidatedCount);
        setLoading(false);
      },
      (error) => {
        console.error("Error during live search:", error);
        setLoading(false);
      }
    );
    
    // Cleanup
    return () => unsubscribe();
  }, [searchTerm]);
  
  const handleSearch = () => {
    if (!searchTerm.trim()) return;
    onSearch(searchTerm);
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Search QR Record</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Enter customer's contact number, name, or QR ID
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <TextField
            autoFocus
            margin="dense"
            label="Contact Number, Name or QR ID"
            type="text"
            fullWidth
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
          {loading ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Searching...
            </Typography>
          ) : matchCount > 0 && (
            <Typography 
              variant="caption" 
              color={matchCount > 20 ? "warning.main" : "success.main"} 
              sx={{ mt: 1, display: 'block' }}
            >
              {matchCount} {matchCount === 1 ? 'record' : 'records'} found
              {matchCount > 20 ? ' - Please refine your search' : ''}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSearch} 
          color="primary" 
          variant="contained"
          disabled={searchTerm.trim().length < 2}
        >
          Search
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Add GenerateAccessDialog component before the QRDetailsDialog component
interface GenerateAccessDialogProps {
  open: boolean;
  onClose: () => void;
  record: QridRecord | null;
}

const GenerateAccessDialog: React.FC<GenerateAccessDialogProps> = ({ open, onClose, record }) => {
  const [date, setDate] = useState<string>(formatDateForInput(new Date()));
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [pickupTime, setPickupTime] = useState<string>('10:30');
  const [returnTime, setReturnTime] = useState<string>('17:30');
  const [accessType, setAccessType] = useState('Collection');
  const [queueDocId, setQueueDocId] = useState<string>('');

  // Format date for input field (YYYY-MM-DD)
  function formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setDate(formatDateForInput(new Date()));
    setPickupTime('10:30');
    setReturnTime('17:30');
    setAccessType('Collection');
    setFormErrors({});
    setSuccess(false);
    setSubmitError('');
    setQueueDocId('');
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Validate date
    if (!date) {
      errors.date = 'Date is required';
    } else {
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        errors.date = 'Cannot select a past date';
      }
    }
    
    // Validate times based on current time
    const now = new Date();
    const selectedDate = new Date(date);
    const isToday = selectedDate.toDateString() === now.toDateString();
    
    // Parse time strings
    const [pickupHours, pickupMinutes] = pickupTime.split(':').map(Number);
    const [returnHours, returnMinutes] = returnTime.split(':').map(Number);
    
    // Create Date objects for comparison
    const pickupDateTime = new Date(selectedDate);
    pickupDateTime.setHours(pickupHours, pickupMinutes, 0, 0);
    
    const returnDateTime = new Date(selectedDate);
    returnDateTime.setHours(returnHours, returnMinutes, 0, 0);
    
    if (isToday) {
      // For today, pickup time must be greater than current time
      if (pickupDateTime.getTime() <= now.getTime()) {
        errors.pickupTime = 'Pickup time must be after current time';
      }
    }
    
    // Return time must be after pickup time
    if (returnDateTime.getTime() <= pickupDateTime.getTime()) {
      errors.returnTime = 'Return time must be after pickup time';
    }
    
    // Time constraints for both same day and future dates
    const minTime = new Date(selectedDate);
    minTime.setHours(10, 30, 0, 0);
    
    const maxTime = new Date(selectedDate);
    maxTime.setHours(19, 30, 0, 0);
    
    if (pickupDateTime.getTime() < minTime.getTime()) {
      errors.pickupTime = 'Pickup time must be after 10:30';
    }
    
    if (pickupDateTime.getTime() > maxTime.getTime()) {
      errors.pickupTime = 'Pickup time must be before 19:30';
    }
    
    if (returnDateTime.getTime() < minTime.getTime()) {
      errors.returnTime = 'Return time must be after 10:30';
    }
    
    if (returnDateTime.getTime() > maxTime.getTime()) {
      errors.returnTime = 'Return time must be before 19:30';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Queue scheduled access generation directly to Firestore for worker v2
  const handleSubmit = async () => {
    if (!record) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError('');

    try {
      const qrid = String(record.Qrid || '').trim();
      if (!qrid) {
        setSubmitError('Missing QRID on this record. Cannot queue scheduled access.');
        return;
      }

      const [yyyy, mm, dd] = date.split('-');
      const targetDate = `${dd}-${mm}-${yyyy}`; // DD-MM-YYYY for worker v2

      // Prevent duplicate pending queue jobs for same QRID/date/mode
      const existingQ = query(
        collection(db, 'AccessGenQueue'),
        where('qrid', '==', qrid),
        where('targetDate', '==', targetDate),
        where('mode', '==', 'scheduled'),
        where('status', '==', 'pending')
      );
      const existing = await getDocs(existingQ);

      let docId = '';
      if (!existing.empty) {
        docId = existing.docs[0].id;
      } else {
        const ref = await addDoc(collection(db, 'AccessGenQueue'), {
          qrid,
          contactNumber: record.Contact_Number || record['Contact Number'] || '',
          customerName: record.Name || '',
          targetDate,
          status: 'pending',
          mode: 'scheduled',
          source: 'admin-webapp-generate-access',
          type: accessType,
          pickupTime,
          returnTime,
          createdAt: serverTimestamp(),
        });
        docId = ref.id;
      }

      setQueueDocId(docId);
      setSuccess(true);

      // Add a small delay before closing the dialog
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error queuing scheduled access generation:', error);
      setSubmitError('Failed to queue scheduled access generation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!record) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Generate Access Pass
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Creating access pass for: <strong>{record.Name}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Contact: <strong>{record.Contact_Number || record["Contact Number"]}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                QR ID / Access Code: <strong>{record.Qrid || 'Not assigned'}</strong>
              </Typography>
              <Box sx={{ mt: 1, p: 1, backgroundColor: 'info.light', color: 'info.contrastText', borderRadius: 1 }}>
                <Typography variant="caption">
                  Note: This QR ID will be automatically used as the Access Code in the system
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Access Type</InputLabel>
                <Select
                  value={accessType}
                  onChange={(e) => setAccessType(e.target.value)}
                  label="Access Type"
                >
                  <MenuItem value="Collection">Collection</MenuItem>
                  <MenuItem value="Return">Return</MenuItem>
                  <MenuItem value="User Request Collection">User Request Collection</MenuItem>
                  <MenuItem value="User Request Return">User Request Return</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!formErrors.date}
                helperText={formErrors.date}
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Pickup Time"
                type="time"
                fullWidth
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!formErrors.pickupTime}
                helperText={formErrors.pickupTime}
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Return Time"
                type="time"
                fullWidth
                value={returnTime}
                onChange={(e) => setReturnTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!formErrors.returnTime}
                helperText={formErrors.returnTime}
              />
            </Grid>
            
            {submitError && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <AlertTitle>Error</AlertTitle>
                  {submitError}
                </Alert>
              </Grid>
            )}
            
            {success && (
              <Grid item xs={12}>
                <Alert severity="success">
                  <AlertTitle>Scheduled access queued!</AlertTitle>
                  <Typography variant="body2">
                    Request was queued to Firestore (`AccessGenQueue`) for worker v2 processing.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>QR ID:</strong> {record.Qrid || 'Not assigned'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Target Date:</strong> {date}
                  </Typography>
                  {queueDocId && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Queue Doc ID:</strong> {queueDocId}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1, color: 'success.dark', fontWeight: 'medium' }}>
                    Access links will be generated by worker and written to EmhubAccessPreGen.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Box sx={{ position: 'relative' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading || success}
            startIcon={success ? <CheckCircleIcon /> : undefined}
          >
            {success ? 'Sent!' : 'Generate Access'}
          </Button>
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
              }}
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// Modify QRDetailsDialog to include a button for generating access passes
const QRDetailsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  record: QridRecord | null;
  onAction: (actionType: 'Pickup' | 'Return' | 'Assist', record: QridRecord, whoCollect?: string) => void;
  setSnackbar: (snackbar: { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }) => void;
}> = ({ open, onClose, record, onAction, setSnackbar }) => {
  const [pickupMethodOpen, setPickupMethodOpen] = useState(false);
  const [invoiceRecords, setInvoiceRecords] = useState<InvoiceRecord[]>([]);
  const [guidanceRecords, setGuidanceRecords] = useState<InvoiceRecord[]>([]);
  const [refundRecords, setRefundRecords] = useState<RefundRecord[]>([]);
  const [emhubAccessPasses, setEmhubAccessPasses] = useState<EmhubAccessPass[]>([]);
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  const [loadingAccessPasses, setLoadingAccessPasses] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [guidanceMenuAnchorEl, setGuidanceMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [refundMenuAnchorEl, setRefundMenuAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Add state for the Generate Access dialog
  const [generateAccessOpen, setGenerateAccessOpen] = useState(false);

  // Add state for the Master Key dialog
  const [masterKeyOpen, setMasterKeyOpen] = useState(false);

  // Add document cache to reduce Firestore reads
  const [documentCache, setDocumentCache] = useState<{
    [qrid: string]: {
      invoices: InvoiceRecord[];
      guidance: InvoiceRecord[];
      refunds: RefundRecord[];
      accessPasses: EmhubAccessPass[];
      timestamp: number;
    }
  }>({});
  
  // Modified fetch effect with caching
  useEffect(() => {
    if (record?.Qrid) {
      const qridString = String(record.Qrid);
      
      // Check cache first
      const cachedData = documentCache[qridString];
      const now = Date.now();
      const cacheValidFor = 5 * 60 * 1000; // 5 minutes in milliseconds
      
      if (cachedData && (now - cachedData.timestamp < cacheValidFor)) {
        console.log(`Using cached data for Qrid ${qridString}, age: ${(now - cachedData.timestamp)/1000}s`);
        // Use cached data
        setInvoiceRecords(cachedData.invoices);
        setGuidanceRecords(cachedData.guidance);
        setRefundRecords(cachedData.refunds);
        setEmhubAccessPasses(cachedData.accessPasses);
        setLoadingPdfs(false);
        setLoadingRefunds(false);
        setLoadingAccessPasses(false);
        return;
      }
      
      // If no valid cache, fetch everything
      setLoadingPdfs(true);
      setLoadingRefunds(true);
      setLoadingAccessPasses(true);
      
      console.log(`QRDetailsDialog: Fetching documents for QR record:`, record);
      
      // Use Promise.all to fetch everything in parallel
      Promise.all([
        getInvoicesByQridAndType(qridString, 'invoice'),
        getInvoicesByQridAndType(qridString, 'guidance'),
        getRefundRecordsByQrid(qridString),
        getEmhubAccessPassesByQrid(qridString)
      ]).then(([invoices, guidanceDocs, refunds, passes]) => {
        console.log(`Fetched ${invoices.length} invoice documents`);
        console.log(`Fetched ${guidanceDocs.length} guidance documents`);
        console.log(`Fetched ${refunds.length} refund documents`);
        console.log(`Fetched ${passes.length} access passes`);
        
        // Update state
        setInvoiceRecords(invoices);
        setGuidanceRecords(guidanceDocs);
        setRefundRecords(refunds);
        setEmhubAccessPasses(passes);
        
        // Cache the results
        const newCacheEntry = {
          invoices,
          guidance: guidanceDocs,
          refunds,
          accessPasses: passes,
          timestamp: now
        };
        
        setDocumentCache(prevCache => {
          const updatedCache = { ...prevCache };
          updatedCache[qridString] = newCacheEntry;
          return updatedCache;
        });
      }).catch(err => {
        console.error('Error fetching documents:', err);
      }).finally(() => {
        setLoadingPdfs(false);
        setLoadingRefunds(false);
        setLoadingAccessPasses(false);
      });
    }
  }, [record, documentCache]); // Added documentCache as a dependency
  
  // Add a cleanup function for the cache
  useEffect(() => {
    // Cleanup function to clear the cache every hour
    const interval = setInterval(() => {
      console.log('Clearing document cache in QRDetailsDialog');
      setDocumentCache({});
    }, 60 * 60 * 1000); // 1 hour
    
    return () => clearInterval(interval);
  }, []);

  const handleAction = (actionType: 'Pickup' | 'Return' | 'Assist') => {
    if (actionType === 'Pickup') {
      setPickupMethodOpen(true);
    } else {
      onAction(actionType, record!);
      onClose();
    }
  };

  const handlePickupMethod = (method: string) => {
    setPickupMethodOpen(false);
    onAction('Pickup', record!, method);
    onClose();
  };
  
  const handleOpenPdf = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };
  
  const handleInvoiceClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleGuidanceClick = (event: React.MouseEvent<HTMLElement>) => {
    setGuidanceMenuAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };
  
  const handleCloseGuidanceMenu = () => {
    setGuidanceMenuAnchorEl(null);
  };
  
  const handleRefundClick = (event: React.MouseEvent<HTMLElement>) => {
    setRefundMenuAnchorEl(event.currentTarget);
  };

  const handleCloseRefundMenu = () => {
    setRefundMenuAnchorEl(null);
  };
  
  // Get appropriate status chip style
  const getStatusStyle = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ready to dispatch':
        return { bgcolor: '#9c27b0', color: 'white' };
      case 'pending return':
        return { bgcolor: '#ff9800', color: 'white' };
      case 'pending refund':
        return { bgcolor: '#f44336', color: 'white' };
      case 'completed':
        return { bgcolor: '#4caf50', color: 'white' };
      default:
        return { bgcolor: '#9e9e9e', color: 'white' };
    }
  };

  // Function to map status to MUI chip color
  const getStatusChipColor = (status: string): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' => {
    switch (status.toLowerCase()) {
      case 'ready to dispatch':
      case 'pending return':
      case 'pending refund':
      case 'pending':
      case 'in progress':
      case 'completed':
        return 'primary'; // Use primary for most statuses
      case 'cancelled':
        return 'default'; // Use default (grey) for cancelled
      default:
        return 'default';
    }
  };
  
  // Format date for display
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return dateStr;
  };

  const parseAccessDate = (dateStr?: string): Date | null => {
    if (!dateStr) return null;
    const value = dateStr.trim();
    if (!value) return null;

    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d);
    }

    // DD-MM-YYYY or DD/MM/YYYY
    if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(value)) {
      const [d, m, y] = value.split(/[-/]/).map(Number);
      return new Date(y, m - 1, d);
    }

    return null;
  };

  const getSlotMinutes = (slotStart?: string): number | null => {
    if (!slotStart || !/^\d{2}:\d{2}$/.test(slotStart)) return null;
    const [h, m] = slotStart.split(':').map(Number);
    return (h * 60) + m;
  };

  const formatCreatedAt = (createdAt: any): string => {
    if (!createdAt) return 'N/A';
    try {
      const dateObj = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
      if (Number.isNaN(dateObj.getTime())) return 'N/A';
      return dateObj.toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const activeAccessPasses = emhubAccessPasses.filter((pass) => {
    const now = new Date();
    const targetDateStr = pass.Date || pass.targetDate || pass.pickupDate;
    const targetDate = parseAccessDate(targetDateStr);
    if (!targetDate) return true;

    const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const passOnly = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());

    if (passOnly < todayOnly) return false;
    if (passOnly > todayOnly) return true;

    const slotMinutes = getSlotMinutes(pass.slotStart);
    if (slotMinutes === null) return true;
    const nowMinutes = (now.getHours() * 60) + now.getMinutes();
    return slotMinutes >= nowMinutes;
  });

  const renderAccessPassesTable = () => (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Target Date</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Slot Time</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {activeAccessPasses.map((pass) => (
            <TableRow key={pass.id} hover>
              <TableCell>{formatDate(pass.Date || pass.targetDate || pass.pickupDate)}</TableCell>
              <TableCell>{pass.slotStart || 'N/A'}</TableCell>
              <TableCell>{formatCreatedAt(pass.createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
  
  if (!record) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            QR Record Details
            <Chip 
              label={record.Status || 'N/A'} 
              size="small"
              sx={{...getStatusStyle(record.Status || ''), borderRadius: '16px'}}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Name</Typography>
              <Typography variant="body1">{record.Name || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Contact Number</Typography>
              <Typography variant="body1">{record.Contact_Number || record["Contact Number"] || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Order ID</Typography>
              <Typography variant="body1">{record.OrderID || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">QR ID</Typography>
              <Typography variant="body1">{record.Qrid || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ color: theme.palette.primary.main }}>Pickup Date</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{record.Pickup_Date || record["Pickup Date"] || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ color: theme.palette.secondary.main }}>Return Date</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{record.Return_Date || record["Return Date"] || 'N/A'}</Typography>
            </Grid>
            
            {/* PDF Documents Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                mt: 2, 
                pt: 2, 
                borderTop: '1px solid', 
                borderColor: 'divider' 
              }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Documents
                </Typography>
                
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {loadingPdfs ? (
                    <Button
                      variant="outlined"
                      startIcon={<CircularProgress size={16} />}
                      disabled
                      size="small"
                    >
                      Loading...
                    </Button>
                  ) : (
                    <>
                      {invoiceRecords.length > 0 ? (
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<PdfIcon />}
                          onClick={invoiceRecords.length === 1 
                            ? () => handleOpenPdf(invoiceRecords[0].shortenLink)
                            : handleInvoiceClick
                          }
                          size="small"
                          endIcon={invoiceRecords.length > 1 ? <ArrowDropDownIcon /> : undefined}
                        >
                          {invoiceRecords.length > 1 
                            ? `${invoiceRecords.length} Invoices` 
                            : 'Invoice'
                          }
                        </Button>
                      ) : null}
                      
                      {guidanceRecords.length > 0 ? (
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<FileCopyIcon />}
                          onClick={guidanceRecords.length === 1 
                            ? () => handleOpenPdf(guidanceRecords[0].shortenLink)
                            : handleGuidanceClick
                          }
                          size="small"
                          endIcon={guidanceRecords.length > 1 ? <ArrowDropDownIcon /> : undefined}
                        >
                          {guidanceRecords.length > 1 
                            ? `${guidanceRecords.length} Guidance` 
                            : 'Guidance'
                          }
                        </Button>
                      ) : null}
                      
                      {/* Add refund records button */}
                      {refundRecords.length > 0 ? (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<ReceiptIcon />}
                          onClick={refundRecords.length === 1 
                            ? () => handleOpenPdf(refundRecords[0].permanentLink || refundRecords[0].shortenLink || '')
                            : handleRefundClick
                          }
                          size="small"
                          endIcon={refundRecords.length > 1 ? <ArrowDropDownIcon /> : undefined}
                        >
                          {refundRecords.length > 1 
                            ? `${refundRecords.length} Refunds` 
                            : `Refund ${refundRecords[0].Invoice_No || ''}`
                          }
                        </Button>
                      ) : null}
                      
                      {(!invoiceRecords.length && !guidanceRecords.length && !refundRecords.length) && (
                        <Typography variant="body2" color="text.secondary">
                          No documents available
                        </Typography>
                      )}
                    </>
                  )}
                </Stack>
                
                {/* Menu for invoice PDFs */}
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={handleCloseMenu}
                >
                  {invoiceRecords.map((invoice) => (
                    <MenuItem 
                      key={invoice.id} 
                      onClick={() => {
                        handleOpenPdf(invoice.shortenLink);
                        handleCloseMenu();
                      }}
                    >
                      <ListItemIcon>
                        <PdfIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={invoice["Invoice No"] || invoice.Invoice_No || 'Invoice'} 
                      />
                    </MenuItem>
                  ))}
                </Menu>
                
                {/* Menu for guidance PDFs */}
                <Menu
                  anchorEl={guidanceMenuAnchorEl}
                  open={Boolean(guidanceMenuAnchorEl)}
                  onClose={handleCloseGuidanceMenu}
                >
                  {guidanceRecords.map((guidance) => (
                    <MenuItem 
                      key={guidance.id} 
                      onClick={() => {
                        handleOpenPdf(guidance.shortenLink);
                        handleCloseGuidanceMenu();
                      }}
                    >
                      <ListItemIcon>
                        <FileCopyIcon fontSize="small" color="secondary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={guidance["Invoice No"] || guidance.Invoice_No || 'Guidance'} 
                      />
                    </MenuItem>
                  ))}
                </Menu>
                
                {/* Menu for refund PDFs */}
                <Menu
                  anchorEl={refundMenuAnchorEl}
                  open={Boolean(refundMenuAnchorEl)}
                  onClose={handleCloseRefundMenu}
                >
                  {refundRecords.map((refund, index) => (
                    <MenuItem 
                      key={refund.id} 
                      onClick={() => {
                        handleOpenPdf(refund.permanentLink || refund.shortenLink || '');
                        handleCloseRefundMenu();
                      }}
                      disabled={!refund.permanentLink && !refund.shortenLink}
                    >
                      <ListItemIcon>
                        <ReceiptIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={refund.Invoice_No || `Refund ${index + 1}`} 
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Grid>
            
            {/* Emhub Access Passes Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                mt: 2, 
                pt: 2, 
                borderTop: '1px solid', 
                borderColor: 'divider' 
              }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Access Passes
                    {activeAccessPasses.length > 0 && 
                      <Chip 
                        label={activeAccessPasses.length} 
                        size="small" 
                        color="primary" 
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                      />
                    }
                  </Typography>
                </Box>
                
                {loadingAccessPasses ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      Loading access passes...
                    </Typography>
                  </Box>
                ) : activeAccessPasses.length === 0 ? (
                  <Box sx={{ 
                    py: 2, 
                    px: 2, 
                    textAlign: 'center',
                    bgcolor: 'background.default',
                    borderRadius: 1
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      No pre-generated access passes found for this QR ID
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {renderAccessPassesTable()}
                  </Box>
                )}
              </Box>
            </Grid>
            
            {isMobile && (
              <Grid item xs={12}>
                <Box sx={{ 
                  mt: 2, 
                  p: 1.5, 
                  bgcolor: 'background.default', 
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: 'divider'
                }}>
                  <Typography variant="subtitle2" gutterBottom>Important Dates:</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="primary">
                      🔹 Pickup: {record.Pickup_Date || record["Pickup Date"] || 'Not set'}
                    </Typography>
                    <Typography variant="body2" color="secondary">
                      🔸 Return: {record.Return_Date || record["Return Date"] || 'Not set'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={onClose}>Close</Button>
          <Box>
            {/* Add Generate Access button */}
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setGenerateAccessOpen(true)}
              sx={{ mr: 1, borderRadius: '20px' }}
            >
              Generate Access
            </Button>
            {/* Add Master Key button */}
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setMasterKeyOpen(true)}
              sx={{ mr: 1, borderRadius: '20px' }}
            >
              Master Key
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAction('Pickup')}
              sx={{ mr: 1, borderRadius: '20px' }}
              disabled={record.Status?.toLowerCase() !== 'ready to dispatch'}
            >
              Pickup
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleAction('Return')}
              sx={{ borderRadius: '20px' }}
              disabled={record.Status?.toLowerCase() !== 'pending return'}
            >
              Return
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add Generate Access Dialog */}
      <GenerateAccessDialog
        open={generateAccessOpen}
        onClose={() => setGenerateAccessOpen(false)}
        record={record}
      />

      {/* Add Master Key Dialog */}
      <Dialog
        open={masterKeyOpen}
        onClose={() => setMasterKeyOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Master Key - Share Access
          <IconButton
            aria-label="close"
            onClick={() => setMasterKeyOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '80vh' }}>
          <Box sx={{ width: '100%', height: '100%' }}>
            <iframe
              src={`https://app.aseasyasrentminiprograms.com/newshareaccess/?qrid=${encodeURIComponent(record.Qrid || '')}`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="Master Key Share Access"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button
            onClick={() => {
              const url = `https://app.aseasyasrentminiprograms.com/newshareaccess/?qrid=${encodeURIComponent(record.Qrid || '')}`;
              navigator.clipboard.writeText(url).then(() => {
                setSnackbar({
                  open: true,
                  message: 'URL copied to clipboard!',
                  severity: 'success'
                });
              }).catch(() => {
                setSnackbar({
                  open: true,
                  message: 'Failed to copy URL',
                  severity: 'error'
                });
              });
            }}
            variant="outlined"
            startIcon={<FileCopyIcon />}
            color="primary"
          >
            Copy URL
          </Button>
          <Button onClick={() => setMasterKeyOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pickupMethodOpen} onClose={() => setPickupMethodOpen(false)}>
        <DialogTitle>Record Pickup Method</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <Button 
              variant="outlined" 
            fullWidth
              onClick={() => handlePickupMethod('Delivery')}
              sx={{ py: 1, borderRadius: '20px' }}
            >
              Delivery
            </Button>
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={() => handlePickupMethod('Self-Collection')}
              sx={{ py: 1, borderRadius: '20px' }}
            >
              Self-Collection
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickupMethodOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { mode, toggleColorMode } = useThemeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileActionStatus = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [activePage, setActivePage] = useState('dashboard');
  const [stats, setStats] = useState({
    total: 0,
    readyToDispatch: 0,
    pendingReturn: 0,
    pendingRefund: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentRecords, setRecentRecords] = useState<any[]>([]);

  // New state values for QR functionality
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<QridRecord[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<QridRecord | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  // New state for the time-based summary
  const [timeBasedStats, setTimeBasedStats] = useState({
    readyToDispatch: { past: 0, today: 0, future: 0 },
    pendingReturn: { past: 0, today: 0, future: 0 },
    pendingRefund: { past: 0, today: 0, future: 0 }
  });
  const [summaryExpanded, setSummaryExpanded] = useState({
    readyToDispatch: false,
    pendingReturn: false,
    pendingRefund: false
  });

  // Remove actionFeedback state, actionTimer and actionListener state variables
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [qridRecords, setQridRecords] = useState<QridRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<QridRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [whoCollect, setWhoCollect] = useState<string>('');

  // Inside the Dashboard component, add a new state variable for pending store actions
  const [pendingStoreActions, setPendingStoreActions] = useState(0);

  // Update state for storing action statuses - add a timeTicker to force UI updates
  const [actionStatuses, setActionStatuses] = useState<{[key: string]: any}>({});
  const [timeTicker, setTimeTicker] = useState(0);
  const actionListenersRef = useRef<{[key: string]: () => void}>({});
  
  // Cleanup function for listeners
  const cleanupActionListeners = () => {
    Object.values(actionListenersRef.current).forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
    actionListenersRef.current = {};
  };

  // Setup real-time listener for a specific record's action status
  const setupActionStatusListener = (record: QridRecord) => {
    // Clean up existing listener for this record if it exists
    if (actionListenersRef.current[record.id]) {
      actionListenersRef.current[record.id]();
    }

    // Get today's start and end timestamps
    const today = new Date();
    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);
    
    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);
    
    // First, try to find actions by traceid (more specific)
    const traceIdQuery = query(
      collection(db, 'StoreActions'),
      where('traceid', '==', record.id),
      where('timestamp', '>=', Timestamp.fromDate(startOfToday)),
      where('timestamp', '<=', Timestamp.fromDate(endOfToday)),
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    
    // Set up a real-time listener
    const unsubscribe = onSnapshot(traceIdQuery, async (snapshot) => {
      if (!snapshot.empty) {
        const actionDoc = snapshot.docs[0];
        const actionData = actionDoc.data();
        const actionStatus = {
          actionType: actionData.actionType,
          status: actionData.actionstatus || 'Pending',
          timestamp: actionData.timestamp,
          crew: actionData.Crew || null,
          id: actionDoc.id,
          inProgressStartTime: actionData.inProgressStartTime || null
        };
        
        // Update the status in state
        setActionStatuses(prevStatuses => ({
          ...prevStatuses,
          [record.id]: actionStatus
        }));
      } else {
        // If no results with traceid, fall back to Qrid
        if (record.Qrid) {
          const qridQuery = query(
            collection(db, 'StoreActions'),
            where('Qrid', '==', record.Qrid),
            where('timestamp', '>=', Timestamp.fromDate(startOfToday)),
            where('timestamp', '<=', Timestamp.fromDate(endOfToday)),
            orderBy('timestamp', 'desc'),
            limit(1)
          );
          
          // One-time query for Qrid as fallback
          const qridSnapshot = await getDocs(qridQuery);
          if (!qridSnapshot.empty) {
            const actionDoc = qridSnapshot.docs[0];
            const actionData = actionDoc.data();
            const actionStatus = {
              actionType: actionData.actionType,
              status: actionData.actionstatus || 'Pending',
              timestamp: actionData.timestamp,
              crew: actionData.Crew || null,
              id: actionDoc.id,
              inProgressStartTime: actionData.inProgressStartTime || null
            };
            
            // Update the status in state
            setActionStatuses(prevStatuses => ({
              ...prevStatuses,
              [record.id]: actionStatus
            }));
          } else {
            // No action found at all
            setActionStatuses(prevStatuses => ({
              ...prevStatuses,
              [record.id]: null
            }));
          }
        }
      }
    }, (error) => {
      console.error("Error listening to action status updates:", error);
    });
    
    // Store the unsubscribe function
    actionListenersRef.current[record.id] = unsubscribe;
  };

  // Update the useEffect for search results to set up real-time listeners
  useEffect(() => {
    if (searchResults.length > 0) {
      // Set up listeners for each search result
      searchResults.forEach(record => {
        setupActionStatusListener(record);
      });
    }
    
    // Cleanup listeners when search results change
    return () => {
      cleanupActionListeners();
    };
  }, [searchResults]);

  // Also update the useEffect for recent records to use real-time listeners
  useEffect(() => {
    if (recentRecords.length > 0) {
      // Set up listeners for each recent record
      recentRecords.forEach(record => {
        setupActionStatusListener(record);
      });
    }
  }, [recentRecords]);

  // Set up interval to force UI updates for timer display (for "In Progress" actions)
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeTicker(prev => prev + 1);
    }, 1000); // Update every second
    
    return () => {
      clearInterval(timer);
      cleanupActionListeners();
    };
  }, []);

  // Add a function to get action status for a QR ID
  const getActionStatus = async (record: QridRecord) => {
    try {
      // Get today's start and end timestamps
      const today = new Date();
      const startOfToday = new Date(today);
      startOfToday.setHours(0, 0, 0, 0);
      
      const endOfToday = new Date(today);
      endOfToday.setHours(23, 59, 59, 999);
      
      // First, try to find actions by traceid (more specific)
      const traceIdQuery = query(
        collection(db, 'StoreActions'),
        where('traceid', '==', record.id),
        where('timestamp', '>=', Timestamp.fromDate(startOfToday)),
        where('timestamp', '<=', Timestamp.fromDate(endOfToday)),
        orderBy('timestamp', 'desc'),
        limit(1)
      );
      
      let snapshot = await getDocs(traceIdQuery);
      
      // If no results with traceid, fall back to Qrid (less specific, but works for older records)
      if (snapshot.empty && record.Qrid) {
        const qridQuery = query(
          collection(db, 'StoreActions'),
          where('Qrid', '==', record.Qrid),
          where('timestamp', '>=', Timestamp.fromDate(startOfToday)),
          where('timestamp', '<=', Timestamp.fromDate(endOfToday)),
          orderBy('timestamp', 'desc'),
          limit(1)
        );
        
        snapshot = await getDocs(qridQuery);
      }
      
      if (!snapshot.empty) {
        const actionDoc = snapshot.docs[0];
        const actionData = actionDoc.data();
        return {
          actionType: actionData.actionType,
          status: actionData.actionstatus || 'Pending',
          timestamp: actionData.timestamp,
          crew: actionData.Crew || null,
          id: actionDoc.id
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting action status:', error);
      return null;
    }
  };

  // Enhance action status chip with a timer for "In Progress" actions
  const renderActionStatusChip = (actionStatus: any) => {
    const getStatusColor = (status: string) => {
      status = status.toLowerCase();
      if (status === 'cancelled') {
        return 'rgba(97, 97, 97, 0.9)'; // Grey for cancelled
      } else if (status === 'completed') {
        return 'rgba(25, 118, 210, 1)'; // Darker blue
      } else if (status === 'in progress') {
        return 'rgba(25, 118, 210, 0.9)'; // Blue
      } else if (status === 'pending') {
        return 'rgba(25, 118, 210, 0.7)'; // Light blue
      } else if (status === 'overdue') {
        return 'rgba(244, 67, 54, 0.9)'; // Red for overdue
      }
      return 'rgba(97, 97, 97, 0.7)'; // Light grey default
    };
    
    const getActionTypeColor = (type: string) => {
      switch (type?.toLowerCase()) {
        case 'pickup':
          return 'rgba(25, 118, 210, 0.9)'; // Blue
        case 'return':
          return 'rgba(25, 118, 210, 0.7)'; // Light blue
        case 'assist':
          return 'rgba(156, 39, 176, 0.8)'; // Purple for assist type (secondary color)
        default:
          return 'rgba(97, 97, 97, 0.7)'; // Grey for unknown type
      }
    };
    
    if (!actionStatus) return null;
    
    // Calculate elapsed time
    let elapsedTime = null;
    let remainingTimePercent = 0;
    let formattedTime = '';
    
    // Fix property name from 'actionstatus' to 'status'
    if (actionStatus.status === 'In Progress' && actionStatus.inProgressStartTime) {
      const startTime = actionStatus.inProgressStartTime.toDate();
      const now = new Date();
      const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000); // in seconds
      
      // Store elapsed time in seconds
      elapsedTime = elapsed;
      
      // Expected completion time (assume 5 minutes for now)
      const expectedCompletionTime = 5 * 60; // 5 minutes in seconds
      remainingTimePercent = Math.min(100, (elapsed / expectedCompletionTime) * 100);
      
      // Format the elapsed time for display
      if (elapsed < 60) {
        formattedTime = `${elapsed}s`;
      } else {
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      }
    }
    
    // Fix property name from 'Crew' to 'crew'
    const actionBy = actionStatus.crew ? `By: ${actionStatus.crew}` : null;
    
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%',
        maxWidth: isMobileActionStatus ? '100%' : 220
      }}>
        {/* Status & Type Row */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 0.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Show icon based on status - fix property name */}
            {actionStatus.status === 'Completed' ? (
              <CheckCircleOutlineIcon 
                fontSize="small" 
                sx={{ 
                  fontSize: isMobileActionStatus ? 16 : 20,
                  color: getStatusColor(actionStatus.status)
                }}
              />
            ) : actionStatus.status === 'In Progress' ? (
              <CircularProgress 
                size={isMobileActionStatus ? 16 : 20} 
                thickness={5}
                sx={{ color: getStatusColor(actionStatus.status) }}
              />
            ) : (
              <FiberManualRecordIcon 
                fontSize="small"
                sx={{ 
                  fontSize: isMobileActionStatus ? 14 : 16,
                  color: getStatusColor(actionStatus.status)
                }}
              />
            )}
            
            {/* Status label - fix property name */}
            <Typography 
              variant="body2" 
              component="span"
              sx={{ 
                fontWeight: 'medium',
                color: getStatusColor(actionStatus.status),
                fontSize: isMobileActionStatus ? '0.75rem' : '0.875rem'
              }}
            >
              {actionStatus.status}
            </Typography>
          </Box>
          
          {/* Action Type Chip */}
          <Chip
            label={actionStatus.actionType}
            size="small"
            sx={{
              height: isMobileActionStatus ? '20px' : '24px',
              fontSize: isMobileActionStatus ? '0.65rem' : '0.75rem',
              backgroundColor: alpha(getActionTypeColor(actionStatus.actionType), 0.1),
              color: getActionTypeColor(actionStatus.actionType),
              fontWeight: 500,
              '& .MuiChip-label': { px: isMobileActionStatus ? 0.8 : 1.2 }
            }}
          />
        </Box>
        
        {/* Crew info */}
        {actionBy && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary',
              fontSize: isMobileActionStatus ? '0.65rem' : '0.75rem',
              ml: isMobileActionStatus ? 2.5 : 3
            }}
          >
            {actionBy}
          </Typography>
        )}
        
        {/* Progress bar for In Progress actions - fix property name */}
        {actionStatus.status === 'In Progress' && elapsedTime !== null && (
          <Box sx={{ mt: 0.5, width: '100%' }}>
            <LinearProgress 
              variant="determinate" 
              value={remainingTimePercent}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  backgroundColor: remainingTimePercent > 80 
                    ? '#f44336' // Use red for overdue/almost overdue
                    : theme.palette.primary.main
                }
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              mt: 0.2
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: isMobileActionStatus ? '0.6rem' : '0.7rem',
                  color: 'text.secondary' 
                }}
              >
                {formattedTime}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: isMobileActionStatus ? '0.6rem' : '0.7rem',
                  color: elapsedTime > 300 ? 'rgba(244, 67, 54, 0.9)' : 'text.secondary' 
                }}
              >
                Est: {elapsedTime > 300 ? 'Overdue' : '5 min'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Effect for stats data
  useEffect(() => {
    // Set up real-time listeners for statistics
    const readyToDispatchQuery = query(
      collection(db, "QridRecord"),
      where("Status", "==", "Ready to Dispatch")
    );
    
    const pendingReturnQuery = query(
      collection(db, "QridRecord"),
      where("Status", "==", "Pending Return")
    );
    
    const pendingRefundQuery = query(
      collection(db, "QridRecord"),
      where("Status", "==", "Pending Refund")
    );
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Helper function to categorize by date
    const categorizeDateByTime = (dateStr: string | null | undefined): 'past' | 'today' | 'future' => {
      if (!dateStr) return 'future'; // Default to future if no date
      
      try {
        // Assuming DD-MM-YYYY format
        const parts = dateStr.split('-');
        if (parts.length !== 3) return 'future';
        
        const date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        date.setHours(0, 0, 0, 0);
        
        if (date < today) return 'past';
        if (date.getTime() === today.getTime()) return 'today';
        return 'future';
      } catch (error) {
        console.error("Error parsing date:", error);
        return 'future';
      }
    };

    const unsubscribeReadyToDispatch = onSnapshot(readyToDispatchQuery, (snapshot) => {
      setStats(prev => ({ ...prev, readyToDispatch: snapshot.size }));
      
      // Categorize by timeframe
      const timeframeStats = { past: 0, today: 0, future: 0 };
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const pickupDate = data["Pickup Date"] || data.Pickup_Date;
        const timeframe = categorizeDateByTime(pickupDate);
        timeframeStats[timeframe]++;
      });
      
      setTimeBasedStats(prev => ({
        ...prev,
        readyToDispatch: timeframeStats
      }));
    });
    
    const unsubscribePendingReturn = onSnapshot(pendingReturnQuery, (snapshot) => {
      setStats(prev => ({ ...prev, pendingReturn: snapshot.size }));
      
      // Categorize by timeframe
      const timeframeStats = { past: 0, today: 0, future: 0 };
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const returnDate = data["Return Date"] || data.Return_Date;
        const timeframe = categorizeDateByTime(returnDate);
        timeframeStats[timeframe]++;
      });
      
      setTimeBasedStats(prev => ({
        ...prev,
        pendingReturn: timeframeStats
      }));
    });
    
    const unsubscribePendingRefund = onSnapshot(pendingRefundQuery, (snapshot) => {
      setStats(prev => ({ ...prev, pendingRefund: snapshot.size }));
      
      // Categorize by timeframe for pending refunds (using return date)
      const timeframeStats = { past: 0, today: 0, future: 0 };
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const returnDate = data["Return Date"] || data.Return_Date;
        const timeframe = categorizeDateByTime(returnDate);
        timeframeStats[timeframe]++;
      });
      
      setTimeBasedStats(prev => ({
        ...prev,
        pendingRefund: timeframeStats
      }));
    });
    
    const unsubscribeTotal = onSnapshot(collection(db, "QridRecord"), (snapshot) => {
      setStats(prev => ({ ...prev, total: snapshot.size }));
      setLoading(false);
    });

    // Get recent records
    const recentQuery = query(
      collection(db, "QridRecord"),
      orderBy("Timestamp", "desc"),
      limit(isMobile ? 50 : 100)
    );

    const unsubscribeRecent = onSnapshot(recentQuery, (snapshot) => {
      const records: any[] = [];
      const qridMap: { [key: string]: any } = {}; // Map to track records by Qrid
      const orderIdMap: { [key: string]: string[] } = {}; // Map to collect OrderIDs by Qrid
      
      snapshot.forEach(doc => {
        const data = doc.data();
        const candidate = {
          id: doc.id,
          ...data,
          Name: data.Name,
          Status: data.Status,
          OrderID: data.OrderID,
          Pickup_Date: data["Pickup Date"],
          Return_Date: data["Return Date"]
        } as QridRecord;
        if (shouldExcludeOldCompletedRecord(candidate)) {
          return;
        }
        const qrid = data.Qrid;
        const orderId = data.OrderID;
        
        if (qrid) {
          // Initialize arrays for collecting OrderIDs if this is the first time seeing this Qrid
          if (!orderIdMap[qrid]) {
            orderIdMap[qrid] = [];
          }
          
          // Add OrderID to the collection if it exists and is not already included
          if (orderId && !orderIdMap[qrid].includes(orderId)) {
            orderIdMap[qrid].push(orderId);
          }
          
          // If this is the first time seeing this Qrid or if this record has a newer timestamp,
          // use this record as the reference
          if (!qridMap[qrid] || 
              (data.Timestamp && 
               qridMap[qrid].Timestamp && 
               data.Timestamp.toDate() > qridMap[qrid].Timestamp.toDate())) {
            
            // Creating a new record to avoid reference issues
            qridMap[qrid] = { 
              id: doc.id, 
              ...data,
              // Map fields
              Name: data.Name,
              Status: data.Status,
              OrderID: data.OrderID, // We'll update this with the combined string later
              Pickup_Date: data["Pickup Date"],
              Return_Date: data["Return Date"]
            };
          }
        } else {
          // If no Qrid, just add as a normal record
          records.push({ 
            id: doc.id, 
            ...data,
            // Map fields
            Name: data.Name,
            Status: data.Status,
            Pickup_Date: data["Pickup Date"],
            Return_Date: data["Return Date"]
          });
        }
      });
      
      // Update OrderIDs to the combined string for each Qrid
      Object.keys(qridMap).forEach(qrid => {
        if (orderIdMap[qrid].length > 0) {
          qridMap[qrid].OrderID = orderIdMap[qrid].join(', ');
        }
        records.push(qridMap[qrid]);
      });
      
      setRecentRecords(records.slice(0, isMobile ? 5 : 10));
    });
    
    // Cleanup function
    return () => {
      unsubscribeReadyToDispatch();
      unsubscribePendingReturn();
      unsubscribePendingRefund();
      unsubscribeTotal();
      unsubscribeRecent();
    };
  }, []);

  // Effect to handle drawer state on screen resize
  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNavigate = (route: string) => {
    if (route === 'admin' && userData?.role === 'admin') {
      navigate('/admin');
    } else if (route === 'server-status') {
      if (userData?.role === 'admin') {
        navigate('/server-status');
      } else {
        setSnackbar({
          open: true,
          message: 'You need admin access to view server status',
          severity: 'error'
        });
      }
    } else if (route === 'settings') {
      navigate('/settings');
    } else if (route === 'logout') {
      handleLogout();
    } else {
      setActivePage(route);
    }
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Status chip color and style
  const getStatusStyle = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ready to dispatch':
        return { bgcolor: '#9c27b0', color: 'white' };
      case 'pending return':
        return { bgcolor: '#ff9800', color: 'white' };
      case 'pending refund':
        return { bgcolor: '#f44336', color: 'white' };
      case 'completed':
        return { bgcolor: '#4caf50', color: 'white' };
      default:
        return { bgcolor: '#9e9e9e', color: 'white' };
    }
  };

  // Function to map status to MUI chip color
  const getStatusChipColor = (status: string): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' => {
    switch (status.toLowerCase()) {
      case 'ready to dispatch':
      case 'pending return':
      case 'pending refund':
      case 'pending':
      case 'in progress':
      case 'completed':
        return 'primary'; // Use primary for most statuses
      case 'cancelled':
        return 'default'; // Use default (grey) for cancelled
      default:
        return 'default';
    }
  };

  // Calculate completion percentage
  const completionPercentage = stats.total > 0 
    ? Math.round((stats.readyToDispatch / stats.total) * 100) 
    : 0;
  
  const pendingPercentage = stats.total > 0 
    ? Math.round((stats.pendingReturn / stats.total) * 100) 
    : 0;

  // Format date for better display
  const formatDate = (timestamp?: any) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Handle Firestore Timestamp
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleDateString();
      }
      
      // Handle JavaScript Date
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString();
      }
      
      // Handle string
      return timestamp.toString();
    } catch (error) {
      console.error("Error formatting date:", error);
      return 'Invalid Date';
    }
  };

  // Function to handle search
  const handleSearch = (searchTerm: string) => {
    if (!searchTerm.trim()) return;

    const term = searchTerm.toLowerCase().trim();
    console.log(`Searching for term: "${term}"`);

    // Query Firestore for matching records
    const recordsRef = collection(db, "QridRecord");

    // Create a query for real-time updates
    onSnapshot(
      query(recordsRef, orderBy("Timestamp", "desc")),
      (snapshot) => {
        console.log(`Total documents in collection: ${snapshot.size}`);

        // Track records at each stage for debugging
        const allRecordsFromDB: QridRecord[] = [];
        const allMatchingRecords: QridRecord[] = [];
        
        // Maps to prevent duplicates
        const qridMap: { [key: string]: QridRecord } = {}; // Map to track records by Qrid
        const orderIdMap: { [key: string]: string[] } = {}; // Map to collect OrderIDs by Qrid
        const matches: QridRecord[] = [];
        const processedQrids = new Set<string>(); // Track processed QR IDs to avoid duplicates
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          const record = {
            id: doc.id,
            ...data
          } as QridRecord;

          if (shouldExcludeOldCompletedRecord(record)) {
            return;
          }
          
          allRecordsFromDB.push(record);
          
          // Match by name, contact number, or QR ID
          const isMatch = 
            (record.Name?.toLowerCase().includes(term)) ||
            (record.Contact_Number?.includes(term)) ||
            (record["Contact Number"]?.includes(term)) ||
            (record.Qrid?.includes(term));
            
          if (isMatch) {
            allMatchingRecords.push(record);
            
            const qrid = record.Qrid;
            
            if (qrid && !processedQrids.has(qrid)) {
              processedQrids.add(qrid); // Mark this Qrid as processed
              
              // Initialize arrays for collecting OrderIDs if this is the first time seeing this Qrid
              if (!orderIdMap[qrid]) {
                orderIdMap[qrid] = [];
              }
              
              // Check all matching records with this Qrid to combine OrderIDs
              allMatchingRecords.filter(r => r.Qrid === qrid).forEach(r => {
                const orderId = r.OrderID;
                // Add OrderID to the collection if it exists and is not already included
                if (orderId && !orderIdMap[qrid].includes(orderId)) {
                  orderIdMap[qrid].push(orderId);
                }
                
                // If this is the first time seeing this Qrid or if this record has a newer timestamp,
                // use this record as the reference
                if (!qridMap[qrid] || 
                    (r.Timestamp && 
                     qridMap[qrid].Timestamp && 
                     r.Timestamp.toDate() > qridMap[qrid].Timestamp.toDate())) {
                  qridMap[qrid] = { ...r };
                }
              });
            } else if (!qrid) {
              // If no Qrid, just add as a normal record
              matches.push(record);
            }
          }
        });
        
        console.log(`All records from DB: ${allRecordsFromDB.length}`);
        console.log(`All matching records before consolidation: ${allMatchingRecords.length}`);
        console.log(`Unique QR IDs found: ${processedQrids.size}`);
        console.log(`Records without QR ID: ${matches.length}`);
        
        // Update OrderIDs to the combined string for each Qrid and add to matches
        Object.keys(qridMap).forEach(qrid => {
          if (orderIdMap[qrid].length > 0) {
            qridMap[qrid].OrderID = orderIdMap[qrid].join(', ');
          }
          matches.push(qridMap[qrid]);
        });
        
        console.log(`Final consolidated matches: ${matches.length}`);
        
        // Log a sample of the final results for debugging
        if (matches.length > 0) {
          console.log("Sample of final results:", 
            matches.slice(0, Math.min(3, matches.length)).map(m => ({
              id: m.id,
              Name: m.Name,
              Qrid: m.Qrid,
              OrderID: m.OrderID
            }))
          );
        }
        
        setSearchResults(matches);
        
        // If only one result, open the details directly
        if (matches.length === 1) {
          setSelectedRecord(matches[0]);
          setDetailsDialogOpen(true);
        } else if (matches.length > 1) {
          // Show notification about multiple results
          setSnackbar({
            open: true,
            message: `Found ${matches.length} matching records`,
            severity: 'info'
          });
        } else {
          // Show notification about no results
          setSnackbar({
            open: true,
            message: 'No matching records found',
            severity: 'warning'
          });
        }
      },
      (error) => {
        console.error("Error searching records:", error);
        setSnackbar({
          open: true,
          message: 'Error searching records',
          severity: 'error'
        });
      }
    );
  };
  
  // Update handleAction to include inProgressStartTime
  const handleAction = async (actionType: 'Pickup' | 'Return' | 'Assist', record: QridRecord, whoCollect?: string) => {
    try {
      // Create the action in Firestore
      await addDoc(collection(db, 'StoreActions'), {
        actionType,
        actionstatus: 'Pending',
        Qrid: record.Qrid,
        name: record.Name, 
        timestamp: serverTimestamp(),
        who_collect: whoCollect || '',
        traceid: record.id, // Add the document ID of the QridRecord as traceid
        inProgressStartTime: null, // Will be set when status changes to 'In Progress'
      });
      
      // Close the details dialog
      setDetailsDialogOpen(false);
      
      // Show snackbar notification
      setSnackbar({
        open: true,
        message: `${actionType} action initiated for ${record.Name}. Check Store Actions to see progress.`,
        severity: 'success',
      });
    } catch (error) {
      console.error(`Error creating ${actionType} action:`, error);
      setSnackbar({
        open: true,
        message: `Failed to create ${actionType} action. Please try again.`,
        severity: 'error',
      });
    }
  };

  // Add an effect to fetch the count of pending store actions
  useEffect(() => {
    // Get today's start and end timestamps
    const today = new Date();
    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);
    
    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);
    
    // Create a query for today's pending actions
    const pendingActionsQuery = query(
      collection(db, 'StoreActions'),
      where('timestamp', '>=', Timestamp.fromDate(startOfToday)),
      where('timestamp', '<=', Timestamp.fromDate(endOfToday)),
      where('actionstatus', '==', 'Pending')
    );
    
    // Set up real-time listener for pending actions count
    const unsubscribe = onSnapshot(
      pendingActionsQuery,
      (snapshot) => {
        setPendingStoreActions(snapshot.size);
      },
      (error) => {
        console.error('Error fetching pending store actions count:', error);
      }
    );
    
    return () => unsubscribe();
  }, []);

  // Function to create an "Assist" ticket
  const createAssistTicket = async () => {
    try {
      await addDoc(collection(db, "StoreActions"), {
        actionType: "Assist",
        name: "Store Assistance", // Default name for Assist tickets
        Qrid: "ASSIST-" + new Date().getTime().toString().slice(-6), // Generate a unique ASSIST ID
        timestamp: serverTimestamp(),
        actionstatus: "Pending" // Set initial status to Pending
      });
      // Show success message
      setSnackbar({
        open: true,
        message: "New 'Assist' ticket created successfully!",
        severity: 'success'
      });
      // No need to refresh the list here as the dashboard doesn't show Store Actions
    } catch (error) {
      console.error("Error creating new ticket:", error);
      setSnackbar({
        open: true,
        message: "Failed to create a new ticket.",
        severity: 'error'
      });
    }
  };

  // Render dashboard content
  const renderContent = () => {
    if (activePage === 'qrid') {
      return (
        <ErrorBoundary>
          <QridManagement />
        </ErrorBoundary>
      );
    } else if (activePage === 'storeactions') {
      return (
        <ErrorBoundary>
          <StoreActions />
        </ErrorBoundary>
      );
    } else if (activePage === 'emhubaccess') {
      return (
        <ErrorBoundary>
          <EmhubAccessPasses />
        </ErrorBoundary>
      );
    } else if (activePage === 'bookings') {
      return (
        <ErrorBoundary>
                          <ModernBookingDashboard />
        </ErrorBoundary>
      );
    } else if (activePage === 'booking-settings') {
      return (
        <ErrorBoundary>
          <BookingFormSettings />
        </ErrorBoundary>
      );
    } else if (activePage === 'todo') {
      return (
        <ErrorBoundary>
          <TodoManagement />
        </ErrorBoundary>
      );
    } else if (activePage === 'seo') {
      return (
        <ErrorBoundary>
          <SeoDashboard />
        </ErrorBoundary>
      );
    }

    return (
      <Box sx={{
        width: '100%',
        p: 3,
        overflowX: 'hidden'
      }}>
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1" 
          sx={{ 
            mb: isMobile ? 2 : 4, 
            textAlign: 'center',
            fontSize: isMobile ? '1.25rem' : undefined
          }}
        >
          Welcome back, {userData?.name || currentUser?.email?.split('@')[0] || 'User'}
        </Typography>
        
        {/* Stats section */}
        <Box sx={{ mb: isMobile ? 2 : 4 }}>
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {/* Ready to Dispatch */}
            <Grid item xs={4} sm={4} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: isMobile ? 1.5 : 2,
                  textAlign: 'center',
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: theme.palette.background.paper
                }}
              >
                <Typography 
                  variant="body1" 
                  color="primary" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: 1,
                    fontSize: isMobile ? '0.9rem' : '1rem'
                  }}
                >
                  <EventNoteIcon sx={{ mr: 1, fontSize: isMobile ? '0.9rem' : '1.2rem', color: 'primary.main' }} />
                  Ready to Dispatch
                </Typography>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  component="div" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#2196f3',
                    lineHeight: 1
                  }}
                >
                  {stats.readyToDispatch || 0}
                </Typography>
                {stats.total > 0 && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={(stats.readyToDispatch / stats.total) * 100} 
                      color="primary"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
            
            {/* Pending Return */}
            <Grid item xs={4} sm={4} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: isMobile ? 1.5 : 2,
                  textAlign: 'center',
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: theme.palette.background.paper
                }}
              >
                <Typography 
                  variant="body1" 
                  color="warning.main" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: 1,
                    fontSize: isMobile ? '0.9rem' : '1rem'
                  }}
                >
                  <LoopIcon sx={{ mr: 1, fontSize: isMobile ? '0.9rem' : '1.2rem', color: 'warning.main' }} />
                  Pending Return
                </Typography>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  component="div" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#ff9800',
                    lineHeight: 1
                  }}
                >
                  {stats.pendingReturn || 0}
                </Typography>
                {stats.total > 0 && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={(stats.pendingReturn / stats.total) * 100} 
                      color="warning"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
            
            {/* Pending Refund */}
            <Grid item xs={4} sm={4} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: isMobile ? 1.5 : 2,
                  textAlign: 'center',
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: theme.palette.background.paper
                }}
              >
                <Typography 
                  variant="body1" 
                  color="error" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: 1,
                    fontSize: isMobile ? '0.9rem' : '1rem'
                  }}
                >
                  <MonetizationOnIcon sx={{ mr: 1, fontSize: isMobile ? '0.9rem' : '1.2rem', color: 'error.main' }} />
                  Pending Refund
                </Typography>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  component="div" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#f44336',
                    lineHeight: 1
                  }}
                >
                  {stats.pendingRefund || 0}
                </Typography>
                {stats.total > 0 && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={(stats.pendingRefund / stats.total) * 100} 
                      color="error"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Individual Status Dropdowns */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {/* Ready to Dispatch Dropdown */}
            <Grid item xs={12} sm={4}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 2, 
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    p: isMobile ? 1 : 1.5, 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)'
                    }
                  }}
                  onClick={() => setSummaryExpanded(prevState => ({
                    ...prevState,
                    readyToDispatch: !prevState.readyToDispatch
                  }))}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: '#bbdefb', 
                        mr: 1,
                        "& .MuiSvgIcon-root": {
                          fontSize: '0.9rem',
                          color: '#1976d2'
                        }
                      }}
                    >
                      <EventAvailableIcon />
                    </Avatar>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        color: '#1976d2'
                      }}
                    >
                      Ready to Dispatch ({stats.readyToDispatch})
                    </Typography>
                  </Box>
                  <ExpandMoreIcon 
                    sx={{ 
                      fontSize: isMobile ? '1.1rem' : '1.3rem',
                      transform: summaryExpanded.readyToDispatch ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 150ms ease',
                      color: '#1976d2'
                    }} 
                  />
                </Box>
                
                <Collapse in={summaryExpanded.readyToDispatch}>
                  <Box sx={{ p: 1.5, backgroundColor: 'rgba(25, 118, 210, 0.02)' }}>
                    <Box
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        flexDirection: 'row', // Always row even on mobile
                        gap: 1
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: isMobile ? 0.75 : 1,
                        borderRadius: 1,
                        backgroundColor: '#f5f9ff',
                        flex: 1,
                        minWidth: 0 // Allow flex shrinking
                      }}>
                        <HistoryIcon sx={{ color: '#90caf9', fontSize: isMobile ? '0.875rem' : '1rem', mr: 0.5, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              fontSize: isMobile ? '0.6rem' : '0.7rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            Before Today
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#1976d2', 
                              fontWeight: 'medium',
                              fontSize: isMobile ? '0.75rem' : undefined
                            }}
                          >
                            {timeBasedStats.readyToDispatch.past}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: isMobile ? 0.75 : 1,
                        borderRadius: 1,
                        backgroundColor: '#e3f2fd',
                        flex: 1,
                        minWidth: 0 // Allow flex shrinking
                      }}>
                        <TodayIcon sx={{ color: '#42a5f5', fontSize: isMobile ? '0.875rem' : '1rem', mr: 0.5, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              fontSize: isMobile ? '0.6rem' : '0.7rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            Today
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#1976d2', 
                              fontWeight: 'medium',
                              fontSize: isMobile ? '0.75rem' : undefined
                            }}
                          >
                            {timeBasedStats.readyToDispatch.today}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: isMobile ? 0.75 : 1,
                        borderRadius: 1,
                        backgroundColor: '#e8f0fe',
                        flex: 1,
                        minWidth: 0 // Allow flex shrinking
                      }}>
                        <UpdateIcon sx={{ color: '#64b5f6', fontSize: isMobile ? '0.875rem' : '1rem', mr: 0.5, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              fontSize: isMobile ? '0.6rem' : '0.7rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            Future
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#1976d2', 
                              fontWeight: 'medium',
                              fontSize: isMobile ? '0.75rem' : undefined
                            }}
                          >
                            {timeBasedStats.readyToDispatch.future}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            </Grid>
          
            {/* Pending Return Dropdown */}
            <Grid item xs={12} sm={4}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 2, 
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    p: isMobile ? 1 : 1.5, 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(237, 108, 2, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(237, 108, 2, 0.08)'
                    }
                  }}
                  onClick={() => setSummaryExpanded(prevState => ({
                    ...prevState,
                    pendingReturn: !prevState.pendingReturn
                  }))}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: '#ffe0b2', 
                        mr: 1,
                        "& .MuiSvgIcon-root": {
                          fontSize: '0.9rem',
                          color: '#ed6c02'
                        }
                      }}
                    >
                      <AccessTimeIcon />
                    </Avatar>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        color: '#ed6c02'
                      }}
                    >
                      Pending Return ({stats.pendingReturn})
                    </Typography>
                  </Box>
                  <ExpandMoreIcon 
                    sx={{ 
                      fontSize: isMobile ? '1.1rem' : '1.3rem',
                      transform: summaryExpanded.pendingReturn ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 150ms ease',
                      color: '#ed6c02'
                    }} 
                  />
                </Box>
                
                <Collapse in={summaryExpanded.pendingReturn}>
                  <Box sx={{ p: 1.5, backgroundColor: 'rgba(237, 108, 2, 0.02)' }}>
                    <Box
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        flexDirection: 'row', // Always row even on mobile
                        gap: 1
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: isMobile ? 0.75 : 1,
                        borderRadius: 1,
                        backgroundColor: '#fff8e1',
                        flex: 1,
                        minWidth: 0 // Allow flex shrinking
                      }}>
                        <HistoryIcon sx={{ color: '#ffb74d', fontSize: isMobile ? '0.875rem' : '1rem', mr: 0.5, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              fontSize: isMobile ? '0.6rem' : '0.7rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            Before Today
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#ed6c02', 
                              fontWeight: 'medium',
                              fontSize: isMobile ? '0.75rem' : undefined
                            }}
                          >
                            {timeBasedStats.pendingReturn.past}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: isMobile ? 0.75 : 1,
                        borderRadius: 1,
                        backgroundColor: '#fffde7',
                        flex: 1,
                        minWidth: 0 // Allow flex shrinking
                      }}>
                        <TodayIcon sx={{ color: '#ffa726', fontSize: isMobile ? '0.875rem' : '1rem', mr: 0.5, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              fontSize: isMobile ? '0.6rem' : '0.7rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            Today
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#ed6c02', 
                              fontWeight: 'medium',
                              fontSize: isMobile ? '0.75rem' : undefined
                            }}
                          >
                            {timeBasedStats.pendingReturn.today}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: isMobile ? 0.75 : 1,
                        borderRadius: 1,
                        backgroundColor: '#fff3e0',
                        flex: 1,
                        minWidth: 0 // Allow flex shrinking
                      }}>
                        <UpdateIcon sx={{ color: '#ffb74d', fontSize: isMobile ? '0.875rem' : '1rem', mr: 0.5, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              fontSize: isMobile ? '0.6rem' : '0.7rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            Future
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#ed6c02', 
                              fontWeight: 'medium',
                              fontSize: isMobile ? '0.75rem' : undefined
                            }}
                          >
                            {timeBasedStats.pendingReturn.future}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            </Grid>
          
            {/* Pending Refund Dropdown */}
            <Grid item xs={12} sm={4}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 2, 
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    p: isMobile ? 1 : 1.5, 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(211, 47, 47, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.08)'
                    }
                  }}
                  onClick={() => setSummaryExpanded(prevState => ({
                    ...prevState,
                    pendingRefund: !prevState.pendingRefund
                  }))}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: '#ffcdd2', 
                        mr: 1,
                        "& .MuiSvgIcon-root": {
                          fontSize: '0.9rem',
                          color: '#d32f2f'
                        }
                      }}
                    >
                      <CalendarTodayIcon />
                    </Avatar>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        color: '#d32f2f'
                      }}
                    >
                      Pending Refund ({stats.pendingRefund})
                    </Typography>
                  </Box>
                  <ExpandMoreIcon 
                    sx={{ 
                      fontSize: isMobile ? '1.1rem' : '1.3rem',
                      transform: summaryExpanded.pendingRefund ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 150ms ease',
                      color: '#d32f2f'
                    }} 
                  />
                </Box>
                
                <Collapse in={summaryExpanded.pendingRefund}>
                  <Box sx={{ p: 1.5, backgroundColor: 'rgba(211, 47, 47, 0.02)' }}>
                    <Box
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        flexDirection: 'row', // Always row even on mobile
                        gap: 1
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: isMobile ? 0.75 : 1,
                        borderRadius: 1,
                        backgroundColor: '#ffebee',
                        flex: 1,
                        minWidth: 0 // Allow flex shrinking
                      }}>
                        <HistoryIcon sx={{ color: '#e57373', fontSize: isMobile ? '0.875rem' : '1rem', mr: 0.5, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              fontSize: isMobile ? '0.6rem' : '0.7rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            Before Today
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#d32f2f', 
                              fontWeight: 'medium',
                              fontSize: isMobile ? '0.75rem' : undefined
                            }}
                          >
                            {timeBasedStats.pendingRefund.past}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: isMobile ? 0.75 : 1,
                        borderRadius: 1,
                        backgroundColor: '#fce4ec',
                        flex: 1,
                        minWidth: 0 // Allow flex shrinking
                      }}>
                        <TodayIcon sx={{ color: '#ef5350', fontSize: isMobile ? '0.875rem' : '1rem', mr: 0.5, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              fontSize: isMobile ? '0.6rem' : '0.7rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            Today
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#d32f2f', 
                              fontWeight: 'medium',
                              fontSize: isMobile ? '0.75rem' : undefined
                            }}
                          >
                            {timeBasedStats.pendingRefund.today}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: isMobile ? 0.75 : 1,
                        borderRadius: 1,
                        backgroundColor: '#ffebee',
                        flex: 1,
                        minWidth: 0 // Allow flex shrinking
                      }}>
                        <UpdateIcon sx={{ color: '#e57373', fontSize: isMobile ? '0.875rem' : '1rem', mr: 0.5, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block', 
                              fontSize: isMobile ? '0.6rem' : '0.7rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            Future
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#d32f2f', 
                              fontWeight: 'medium',
                              fontSize: isMobile ? '0.75rem' : undefined
                            }}
                          >
                            {timeBasedStats.pendingRefund.future}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mb: isMobile ? 2 : 4 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: isMobile ? 1.5 : 2,
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              fontWeight: 'medium'
            }}
          >
            Quick Actions
          </Typography>
          
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {/* Search QR Record */}
            <Grid item xs={12} sm={6} md={4}>
              <Card 
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                  background: alpha(theme.palette.primary.main, 0.03),
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                    borderColor: 'transparent'
                  }
                }}
                onClick={() => setSearchDialogOpen(true)}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  py: isMobile ? 1.5 : 2,
                  px: isMobile ? 2 : 3,
                  '&:last-child': { pb: isMobile ? 1.5 : 2 }
                }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.9), 
                      width: isMobile ? 40 : 50, 
                      height: isMobile ? 40 : 50, 
                      mr: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
                    }}
                  >
                    <QrCodeScannerIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="div" 
                      sx={{ 
                        fontWeight: 'medium',
                        fontSize: isMobile ? '1rem' : '1.25rem',
                        mb: 0.5
                      }}
                    >
                      Search QR Record
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}
                    >
                      Find and process customer QR records
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* View All Records */}
            <Grid item xs={12} sm={6} md={4}>
              <Card 
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                  background: alpha(theme.palette.primary.main, 0.03),
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                    borderColor: 'transparent'
                  }
                }}
                onClick={() => setActivePage('qrid')}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  py: isMobile ? 1.5 : 2,
                  px: isMobile ? 2 : 3,
                  '&:last-child': { pb: isMobile ? 1.5 : 2 }
                }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.9), 
                      width: isMobile ? 40 : 50, 
                      height: isMobile ? 40 : 50, 
                      mr: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
                    }}
                  >
                    <StorageIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="div" 
                      sx={{ 
                        fontWeight: 'medium',
                        fontSize: isMobile ? '1rem' : '1.25rem',
                        mb: 0.5
                      }}
                    >
                      View All Records
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}
                    >
                      Access all QR code records
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Request Assistance */}
            <Grid item xs={12} sm={6} md={4}>
              <Card 
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                  background: alpha(theme.palette.secondary.main, 0.03),
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                    borderColor: 'transparent'
                  }
                }}
                onClick={createAssistTicket}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  py: isMobile ? 1.5 : 2,
                  px: isMobile ? 2 : 3,
                  '&:last-child': { pb: isMobile ? 1.5 : 2 }
                }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(theme.palette.secondary.main, 0.9), 
                      width: isMobile ? 40 : 50, 
                      height: isMobile ? 40 : 50, 
                      mr: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
                    }}
                  >
                    <HelpOutlineIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="div" 
                      sx={{ 
                        fontWeight: 'medium',
                        fontSize: isMobile ? '1rem' : '1.25rem',
                        mb: 0.5
                      }}
                    >
                      Assist
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}
                    >
                      Request staff assistance
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Search Results if any */}
        {searchResults.length > 0 && (
          <Paper sx={{ mt: 3, p: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Search Results ({searchResults.length})</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Pickup Date</TableCell>
                    <TableCell>Return Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((record) => (
                    <TableRow 
                      key={record.id} 
                      hover
                      onClick={() => {
                        setSelectedRecord(record);
                        setDetailsDialogOpen(true);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{record.Name}</TableCell>
                      <TableCell>{record.Contact_Number || record["Contact Number"]}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.Pickup_Date || record["Pickup Date"] || 'N/A'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.Return_Date || record["Return Date"] || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={record.Status || 'N/A'}
                          size="small"
                          sx={getStatusStyle(record.Status?.toLowerCase() || '')}
                        />
                      </TableCell>
                      <TableCell>
                        {actionStatuses[record.id] ? 
                          renderActionStatusChip(actionStatuses[record.id]) : 
                          null
                        }
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRecord(record);
                            setDetailsDialogOpen(true);
                          }}
                          sx={{
                            py: 0, 
                            px: 1.5,
                            minWidth: 'unset',
                            fontSize: '0.75rem' 
                          }}
                        >
                          VIEW
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Recent QR Records Section */}
        <Box sx={{ mt: isMobile ? 2 : 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isMobile ? 1 : 2 }}>
            <Typography variant="h5" sx={{ fontSize: isMobile ? '1.1rem' : undefined }}>Recent QR Records</Typography>
            <Button 
              variant="outlined" 
              endIcon={<QrCodeIcon />} 
              onClick={() => setActivePage('qrid')}
              sx={{ 
                borderRadius: '20px',
                py: isMobile ? 0.5 : undefined,
                px: isMobile ? 1.5 : undefined,
                fontSize: isMobile ? '0.8rem' : undefined
              }}
            >
              View All Records
            </Button>
          </Box>
          
          {/* Responsive layout - cards for mobile, table for desktop */}
          {isMobile ? (
            // Mobile view - Card layout
            <Box sx={{ mb: 4 }}>
              {recentRecords.length === 0 ? (
                <Paper sx={{ p: 3, borderRadius: '16px', textAlign: 'center', bgcolor: 'background.paper' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                    <SearchIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No recent records found
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                recentRecords.map((record, index) => (
                  <Paper
                    key={record.id || index}
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: '16px',
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        borderColor: 'transparent'
                      }
                    }}
                    onClick={() => {
                      setSelectedRecord(record);
                      setDetailsDialogOpen(true);
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 'medium',
                          maxWidth: '70%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {record.Name}
                      </Typography>
                      <Chip
                        label={record.Status || 'N/A'}
                        size="small"
                        color={getStatusChipColor(record.Status?.toLowerCase() || '')}
                        sx={{
                          height: '24px',
                          fontSize: '0.7rem',
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    </Box>
                    
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Pickup Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {formatDate(record.Pickup_Date) || formatDate(record["Pickup Date"]) || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Return Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {formatDate(record.Return_Date) || formatDate(record["Return Date"]) || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    {/* Improved Action Status section */}
                    <Box sx={{ 
                      borderTop: '1px solid', 
                      borderColor: 'divider', 
                      pt: 1.5, 
                      mt: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}>
                      <Box sx={{ 
                        flex: 1,
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {actionStatuses[record.id] ? (
                          renderActionStatusChip(actionStatuses[record.id])
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No active actions
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRecord(record);
                          setDetailsDialogOpen(true);
                        }}
                        sx={{
                          py: 0.5,
                          px: 2,
                          minWidth: 'unset',
                          fontSize: '0.75rem',
                          borderRadius: '12px',
                          ml: 1
                        }}
                      >
                        VIEW
                      </Button>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          ) : (
            // Desktop view - Table layout
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <TableContainer sx={{ 
                maxHeight: '50vh',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                }
              }}>
                <Table size="medium" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow sx={{ 
                      backgroundColor: theme.palette.mode === 'light' ? '#f5f7fa' : 'inherit',
                      '& th': { 
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis' 
                      }
                    }}>
                      <TableCell sx={{ width: '15%' }}>Name</TableCell>
                      <TableCell sx={{ width: '15%' }}>Timestamp</TableCell>
                      <TableCell sx={{ width: '12%' }}>Pickup Date</TableCell>
                      <TableCell sx={{ width: '12%' }}>Return Date</TableCell>
                      <TableCell sx={{ width: '10%' }}>Status</TableCell>
                      <TableCell sx={{ width: '18%' }}>Action Status</TableCell>
                      <TableCell align="right" sx={{ width: '8%' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentRecords.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                            <SearchIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              No recent records found
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentRecords.map((record, index) => (
                        <TableRow 
                          key={record.id || index} 
                          hover
                          onClick={() => {
                            setSelectedRecord(record);
                            setDetailsDialogOpen(true);
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            '&:nth-of-type(odd)': {
                              backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'inherit',
                            },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
                            }
                          }}
                        >
                          <TableCell sx={{ 
                            maxWidth: 0, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                          }}>
                            {record.Name}
                          </TableCell>
                          <TableCell sx={{ 
                            maxWidth: 0, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap'
                          }}>
                            {record.Timestamp && record.Timestamp.toDate ? 
                              `${record.Timestamp.toDate().toLocaleDateString()} ${record.Timestamp.toDate().toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}` 
                              : 'N/A'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(record.Pickup_Date) || formatDate(record["Pickup Date"]) || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(record.Return_Date) || formatDate(record["Return Date"]) || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={record.Status || 'N/A'}
                              size="small"
                              color={getStatusChipColor(record.Status?.toLowerCase() || '')}
                              sx={{
                                height: '22px',
                                fontSize: '0.75rem',
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ 
                            verticalAlign: 'top',
                            padding: '8px 8px'
                          }}>
                            {actionStatuses[record.id] ? 
                              renderActionStatusChip(actionStatuses[record.id]) : 
                              null
                            }
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRecord(record);
                                setDetailsDialogOpen(true);
                              }}
                              sx={{
                                py: 0, 
                                px: 1.5,
                                minWidth: 'unset',
                                fontSize: '0.75rem',
                                borderRadius: '12px'
                              }}
                            >
                              DETAILS
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', maxWidth: '100vw', overflow: 'hidden' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <StyledAppBar position="fixed">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={toggleDrawer}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" fontWeight="500">
            {activePage === 'dashboard' ? 'Dashboard' :
             activePage === 'qrid' ? 'QR Records' :
             activePage === 'storeactions' ? 'Store Actions' :
             activePage === 'emhubaccess' ? 'Access Passes' :
             activePage === 'todo' ? 'To-Do Tasks' :
             activePage === 'seo' ? 'SEO Monitor' :
             activePage.charAt(0).toUpperCase() + activePage.slice(1)}
          </Typography>
          
          <Box sx={{ ml: 'auto' }}>
            {!isMobile && (
              <Chip
                avatar={
                  <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                    {userData?.name?.charAt(0)?.toUpperCase() || currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                }
                label={userData?.role === 'admin' ? "Administrator" : "User"}
                variant="outlined"
                size="small"
                sx={{ 
                  borderColor: 'rgba(0,0,0,0.12)',
                  height: 32,
                  borderRadius: '16px'
                }}
              />
            )}
          </Box>
        </Toolbar>
      </StyledAppBar>
      
      {/* Figma Sidebar */}
      <FigmaSidebar
        open={drawerOpen}
        onClose={toggleDrawer}
        currentView={activePage}
        onNavigate={handleNavigate}
        toggleColorMode={toggleColorMode}
        mode={mode}
        userData={userData}
        currentUser={currentUser}
        isMobile={isMobile}
      />
      
      {/* Main content */}
      <Box 
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          overflowX: 'hidden'
        }}
      >
        <Toolbar /> {/* Space for app bar */}
        <MainContent>
          {renderContent()}
        </MainContent>
      </Box>
      
      {/* Dialogs for QR functionality */}
      <SearchQRDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        onSearch={handleSearch}
        setSnackbar={setSnackbar}
      />
      
      <QRDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        record={selectedRecord}
        onAction={handleAction}
        setSnackbar={setSnackbar}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard; 
